<template>
  <div class="franchises-subaccounts-page">
    <div class="page-header">
      <h1>{{ t('Subcontas de Pagamento') }}</h1>
      <p class="page-subtitle">{{ t('Subcontas criadas via conta master para franquias') }}</p>
      <div class="header-actions">
        <Button
          data-testid="subaccounts-new-account-button"
          :label="t('Nova Subconta')"
          icon="pi pi-plus"
          class="p-button-success"
          @click="handleNewAccount"
        />
      </div>
    </div>

    <div class="page-content">
      <!-- Accounts table - handles all data loading internally -->
      <FranchisesSubaccountsTable
        ref="tableRef"
        @edit="handleEdit"
        @config="handleConfig"
        @fiscal="handleFiscalConfig"
      />
    </div>

    <!--
      NOTE: Modals are NOT imported here - they are centralized in ModalManager.vue
      Use eventBus.emit('modal.paymentaccount.open', {...}) to open modals
    -->
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;

interface SubaccountRecord {
  id: Id;
}

interface TableInstance {
  refresh?: () => void;
}

const { t } = useI18n();

// State
const tableRef = ref<TableInstance | null>(null);

// Methods
const handleNewAccount = (): void => {
  log('FranchisesSubaccountsPage', 'handleNewAccount', 'Opening new account wizard');
  eventBus.emit('modal.franchisessubaccount.wizard.open', { mode: 'create' });
};

const handleEdit = (account: SubaccountRecord): void => {
  log('FranchisesSubaccountsPage', 'handleEdit', 'Editing account', account.id);
  eventBus.emit('modal.franchisessubaccount.wizard.open', {
    mode: 'edit',
    account_id: account.id
  });
};

const handleConfig = (account: SubaccountRecord): void => {
  log('FranchisesSubaccountsPage', 'handleConfig', 'Opening config wizard', account.id);
  // Open wizard in edit mode - it will start at step 0 but user can navigate to config step
  eventBus.emit('modal.franchisessubaccount.wizard.open', {
    mode: 'edit',
    account_id: account.id
  });
};

const handleFiscalConfig = (account: SubaccountRecord): void => {
  log('FranchisesSubaccountsPage', 'handleFiscalConfig', 'Opening fiscal config', account.id);
  eventBus.emit('modal.franchisesfiscalconfig.open', {
    account_id: account.id
  });
};
</script>

<style scoped>
.franchises-subaccounts-page {
  padding: 1.5rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.page-header h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.page-subtitle {
  margin: 0.25rem 0 0 0;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}
</style>
